import { Logo } from "@ondit/tokens/logo";
import { BrandLoading, Button, NativeSelect, ToastProvider } from "@ondit/ui";
import {
	Activity,
	Building2,
	ClipboardList,
	Coins,
	FileText,
	Gauge,
	Inbox,
	Layers,
	ListTree,
	LogOut,
	Quote,
	ScrollText,
	Users,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router";
import { Async, ConfirmProvider } from "@/components/ui";
import { I18nProvider, type Locale, readLocale, useI18n, useT, writeLocale } from "@/i18n";
import { api, type Me } from "@/lib/api";
import { useAsync } from "@/lib/hooks";
import { AuditPage } from "@/pages/audit";
import { CataloguePage } from "@/pages/catalogue";
import { CitationsPage } from "@/pages/citations";
import { EntitiesPage, EntityPage } from "@/pages/entities";
import { JobPage, JobsPage } from "@/pages/jobs";
import { LoginPage } from "@/pages/login";
import { ModelCallsPage } from "@/pages/model-calls";
import { OrgPage, OrgsPage } from "@/pages/orgs";
import { OverviewPage } from "@/pages/overview";
import { PageDetailPage, PagesPage } from "@/pages/pages";
import { PlansPage } from "@/pages/plans";
import { SubmissionsPage } from "@/pages/submissions";
import { UsagePage } from "@/pages/usage";
import { UserPage, UsersPage } from "@/pages/users";
import { readTheme, type Theme, writeTheme } from "@/theme";

export function App() {
	const [locale, setLocaleState] = useState<Locale>(() => readLocale());
	const setLocale = useCallback((next: Locale) => {
		setLocaleState(next);
		writeLocale(next);
	}, []);
	useEffect(() => {
		document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
	}, [locale]);

	return (
		<I18nProvider locale={locale} setLocale={setLocale}>
			<ConfirmProvider>
				<Routes>
					<Route path="/login" element={<LoginPage />} />
					<Route path="/*" element={<Shell />} />
				</Routes>
			</ConfirmProvider>
			<ToastProvider />
		</I18nProvider>
	);
}

function Shell() {
	const t = useT();
	const me = useAsync<Me>(() => api.me(), []);
	return (
		<Async
			state={me}
			pending={
				<BrandLoading label={t("state.loading")} slowLabel={t("state.loadingSlow")}>
					<Logo variant="mark" />
				</BrandLoading>
			}
		>
			{(data) => (
				<Layout me={data}>
					<Routes>
						<Route index element={<OverviewPage />} />
						<Route path="citations" element={<CitationsPage />} />
						<Route path="usage" element={<UsagePage />} />
						<Route path="users" element={<UsersPage />} />
						<Route path="users/:id" element={<UserPage />} />
						<Route path="orgs" element={<OrgsPage />} />
						<Route path="orgs/:id" element={<OrgPage />} />
						<Route path="entities" element={<EntitiesPage />} />
						<Route path="entities/:id" element={<EntityPage />} />
						<Route path="submissions" element={<SubmissionsPage />} />
						<Route path="pages" element={<PagesPage />} />
						<Route path="pages/:id" element={<PageDetailPage />} />
						<Route path="jobs" element={<JobsPage />} />
						<Route path="jobs/:id" element={<JobPage />} />
						<Route path="plans" element={<PlansPage />} />
						<Route path="model-calls" element={<ModelCallsPage />} />
						<Route path="catalogue" element={<CataloguePage />} />
						<Route path="audit" element={<AuditPage />} />
						<Route path="*" element={<Navigate to="/" replace />} />
					</Routes>
				</Layout>
			)}
		</Async>
	);
}

function Layout({ me, children }: { me: Me; children: ReactNode }) {
	const t = useT();
	const { locale, setLocale } = useI18n();
	const [theme, setThemeState] = useState<Theme>(() => readTheme());
	const setTheme = (next: Theme) => {
		writeTheme(next);
		setThemeState(next);
	};

	async function signOut() {
		await api.logout();
		window.location.assign("/login");
	}

	return (
		<div className="layout">
			<a className="skip-link" href="#main-content">
				{t("nav.skipContent")}
			</a>
			<nav className="sidebar">
				<div className="sidebar-head">
					<Logo size="md" />
					<span className="tag">{t("app.name")}</span>
				</div>
				<div className="nav-scroll">
					<div className="nav-group">
						<Item to="/" end icon={<Gauge />} label={t("nav.overview")} />
					</div>
					<div className="nav-group">
						<h2>{t("nav.group.measure")}</h2>
						<Item to="/citations" icon={<Quote />} label={t("nav.citations")} />
						<Item to="/usage" icon={<Activity />} label={t("nav.usage")} />
					</div>
					<div className="nav-group">
						<h2>{t("nav.group.customers")}</h2>
						<Item to="/orgs" icon={<Building2 />} label={t("nav.orgs")} />
						<Item to="/users" icon={<Users />} label={t("nav.users")} />
					</div>
					<div className="nav-group">
						<h2>{t("nav.group.directory")}</h2>
						<Item to="/entities" icon={<ListTree />} label={t("nav.entities")} />
						<Item to="/submissions" icon={<Inbox />} label={t("nav.submissions")} />
						<Item to="/pages" icon={<FileText />} label={t("nav.pages")} />
					</div>
					<div className="nav-group">
						<h2>{t("nav.group.operations")}</h2>
						<Item to="/jobs" icon={<ClipboardList />} label={t("nav.jobs")} />
						<Item to="/model-calls" icon={<Coins />} label={t("reconcile.title")} />
						<Item to="/audit" icon={<ScrollText />} label={t("nav.audit")} />
					</div>
					<div className="nav-group">
						<h2>{t("nav.group.catalogue")}</h2>
						<Item to="/plans" icon={<Coins />} label={t("nav.plans")} />
						<Item to="/catalogue" icon={<Layers />} label={t("nav.catalogue")} />
					</div>
				</div>
				<div className="sidebar-foot">
					<span className="who" title={me.user.email}>
						{me.user.name || me.user.email}
					</span>
					<div className="row">
						<NativeSelect
							aria-label={t("nav.language")}
							value={locale}
							onChange={(e) => setLocale(e.target.value as Locale)}
						>
							<option value="en">English</option>
							<option value="zh">中文</option>
						</NativeSelect>
						<NativeSelect aria-label={t("nav.theme")} value={theme} onChange={(e) => setTheme(e.target.value as Theme)}>
							<option value="system">{t("nav.themeSystem")}</option>
							<option value="light">{t("nav.themeLight")}</option>
							<option value="dark">{t("nav.themeDark")}</option>
						</NativeSelect>
					</div>
					<div className="row">
						<Button asChild variant="ghost">
							<a href={me.origins.app} target="_blank" rel="noreferrer">
								{t("nav.openPanel")}
							</a>
						</Button>
						<Button asChild variant="ghost">
							<a href={me.origins.public} target="_blank" rel="noreferrer">
								{t("nav.openSite")}
							</a>
						</Button>
					</div>
					<Button variant="ghost" type="button" onClick={signOut}>
						<LogOut />
						{t("nav.signOut")}
					</Button>
				</div>
			</nav>
			<main id="main-content" className="content" tabIndex={-1}>
				{children}
			</main>
		</div>
	);
}

export function Item({ to, label, end, icon }: { to: string; label: string; end?: boolean; icon: ReactNode }) {
	return (
		<NavLink to={to} end={end} className="nav-item">
			{icon}
			<span>{label}</span>
		</NavLink>
	);
}
